import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Eye, X, type LucideIcon } from 'lucide-react';
import { MODEL_ICON, SOURCE_META, SPACE_ICON } from '../dataModelsConstants';
import type { DataSourceKind, PublishStatus, SpaceIconKey, SpaceStatus } from '../dataModelsTypes';

export function surfaces(isLight: boolean) {
  return {
    page: isLight ? 'bg-[#F4F6F8]' : 'bg-black',
    header: isLight ? 'bg-white' : 'bg-black',
    rail: isLight ? 'bg-white' : 'bg-black',
    railHeader: isLight ? 'bg-white' : 'bg-black',
    body: isLight ? 'bg-white' : 'bg-black',
    card: isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-black',
    border: isLight ? 'border-[#E6E8EB]' : 'border-white/10',
    selected: isLight ? 'bg-[#E7F2FB] text-[#16324F]' : 'bg-white/10 text-white',
    muted: isLight ? 'text-[#6B7280]' : 'text-slate-400',
    tableHead: isLight ? 'bg-[#F7F8FA] text-[#5C6570]' : 'bg-white/[0.04] text-slate-400',
    rowAlt: isLight ? 'bg-[#FAFBFC] hover:bg-[#F3F7FB]' : 'bg-white/[0.02] hover:bg-white/[0.04]',
    row: isLight ? 'bg-white hover:bg-[#F7F9FB]' : 'hover:bg-white/[0.04]',
    tabBar: isLight ? 'bg-white' : 'bg-black',
    accent: 'bg-[#1B6CA8]',
    accentText: isLight ? 'text-[#1C1C1C]' : 'text-white',
  };
}

export function fieldClass(isLight: boolean): string {
  return isLight
    ? 'w-full rounded-md border border-[#D8DCE2] bg-white px-3 py-2 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none focus:ring-2 focus:ring-[#1B6CA8]/15'
    : 'w-full rounded-md border border-white/15 bg-black px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/10';
}

export function searchClass(isLight: boolean): string {
  return isLight
    ? 'h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-3 text-sm text-[#1C1C1C] placeholder:text-[#9AA1AB] focus:border-[#1B6CA8] focus:outline-none focus:ring-2 focus:ring-[#1B6CA8]/15'
    : 'h-8 w-full rounded-md border border-white/15 bg-black py-0 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/10';
}

export function labelClass(isLight: boolean): string {
  return isLight ? 'mb-1.5 block text-[13px] font-medium text-[#3C4048]' : 'mb-1.5 block text-[13px] font-medium text-slate-300';
}

export function primaryBtn(extra = ''): string {
  return `inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-[#4B5563] px-3 text-[13px] font-medium !text-white hover:bg-[#3C4048] hover:!text-white disabled:pointer-events-none disabled:opacity-50 ${extra}`;
}

export function ghostBtn(isLight: boolean): string {
  return isLight
    ? 'inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#D0D5DD] bg-white px-3 text-[13px] font-medium text-[#3C4048] hover:bg-[#F7F8FA] disabled:pointer-events-none disabled:opacity-40'
    : 'inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-white/15 bg-transparent px-3 text-[13px] font-medium text-slate-200 hover:bg-white/5 disabled:pointer-events-none disabled:opacity-40';
}

export function iconBtn(isLight: boolean): string {
  return isLight
    ? 'inline-flex h-7 w-7 items-center justify-center rounded-md text-[#5C6570] hover:bg-[#F2F4F7]'
    : 'inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-300 hover:bg-white/10';
}

export function ViewRowButton({
  isLight,
  label,
  onClick,
}: {
  isLight: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={iconBtn(isLight)}
      title={label}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Eye size={14} />
    </button>
  );
}

export function IconTile({
  icon: Icon,
  isLight,
  size = 15,
  box = 'h-7 w-7',
}: {
  icon: LucideIcon;
  tone?: string;
  dark?: string;
  isLight: boolean;
  size?: number;
  box?: string;
}) {
  return (
    <span
      className={`inline-flex ${box} shrink-0 items-center justify-center rounded-md ${
        isLight ? 'bg-[#F2F4F7] text-[#4B5563]' : 'bg-white/10 text-slate-300'
      }`}
    >
      <Icon size={size} strokeWidth={1.75} />
    </span>
  );
}

export function SpaceIconTile({ icon, isLight, box = 'h-6 w-6' }: { icon: SpaceIconKey; isLight: boolean; box?: string }) {
  return <IconTile icon={SPACE_ICON[icon]} isLight={isLight} size={14} box={box} />;
}

export function ModelIconTile({ isLight, box = 'h-6 w-6' }: { isLight: boolean; box?: string }) {
  return <IconTile icon={MODEL_ICON} isLight={isLight} size={14} box={box} />;
}

export function SourceIconTile({ kind, isLight, box = 'h-6 w-6' }: { kind: DataSourceKind; isLight: boolean; box?: string }) {
  const meta = SOURCE_META[kind] ?? SOURCE_META.postgresql;
  return <IconTile icon={meta.icon} isLight={isLight} size={13} box={box} />;
}

export function StatusPill({ status, isLight }: { status: PublishStatus | SpaceStatus; isLight: boolean }) {
  const label = status === 'active' ? 'Active' : status === 'published' ? 'Published' : status === 'archived' ? 'Archived' : 'Draft';
  const tone = isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200';
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>;
}

export function tagTone(_tag: string, isLight: boolean): string {
  return isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200';
}

export function Menu({
  open,
  onClose,
  children,
  isLight,
  align = 'right',
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLight: boolean;
  align?: 'left' | 'right';
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      ref={root}
      className={`absolute z-30 mt-1 min-w-[168px] rounded-md border py-1 shadow-md ${align === 'right' ? 'right-0' : 'left-0'} ${
        isLight ? 'border-[#E6E8EB] bg-white shadow-slate-200/80' : 'border-white/10 bg-black'
      }`}
    >
      {children}
    </div>
  );
}

export function MenuItem({
  onClick,
  children,
  isLight,
}: {
  onClick: () => void;
  children: React.ReactNode;
  isLight: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] ${
        isLight ? 'text-[#1C1C1C] hover:bg-[#F3F7FB]' : 'text-slate-200 hover:bg-white/5'
      }`}
    >
      {children}
    </button>
  );
}

export function DialogShell({
  title,
  subtitle,
  isLight,
  onClose,
  children,
  footer,
  wide,
  size,
}: {
  title: string;
  subtitle?: string;
  isLight: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  wide?: boolean;
  size?: 'default' | 'wide' | 'medium' | 'large' | 'full' | 'page';
}) {
  const isPage = size === 'page';
  const width = isPage
    ? 'w-[calc(100vw-1.5rem)]'
    : size === 'full'
      ? 'w-[min(1180px,calc(100vw-2rem))]'
      : size === 'large'
        ? 'w-[min(920px,calc(100vw-2rem))]'
        : size === 'medium'
          ? 'max-w-2xl'
          : wide || size === 'wide'
            ? 'max-w-lg'
            : 'max-w-md';
  const frame = isPage ? 'h-[calc(100vh-1.5rem)] flex flex-col' : '';
  const bodyHeight = isPage
    ? 'min-h-0 flex-1'
    : size === 'full' || size === 'large'
      ? 'max-h-[calc(100vh-9rem)]'
      : 'max-h-[65vh]';
  const bodyPad = isPage || size === 'full' || size === 'large' ? 'overflow-hidden p-0' : 'space-y-3 overflow-y-auto px-4 py-4';
  return createPortal(
    <div className={`fixed inset-0 z-[240] flex items-center justify-center ${isPage ? 'p-3' : 'p-4'}`}>
      <button type="button" className="absolute inset-0 bg-[#0F172A]/40" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full ${width} ${frame} overflow-hidden rounded-lg border shadow-xl ${
          isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-black'
        }`}
      >
        <div className={`flex shrink-0 items-start justify-between gap-3 border-b px-4 py-3 ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
          <div className="min-w-0">
            <h2 className={`text-[15px] font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{title}</h2>
            {subtitle ? <p className={`mt-0.5 text-[13px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{subtitle}</p> : null}
          </div>
          <button type="button" className={iconBtn(isLight)} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className={`${bodyHeight} ${bodyPad}`}>{children}</div>
        <div className={`flex shrink-0 justify-end gap-2 border-t px-4 py-3 ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-black'}`}>
          {footer}
        </div>
      </div>
    </div>,
    document.body
  );
}
